import { callVisionModel, extractJson, SitePlanError } from '@/reference/SitePlanClient';
import { defaultModelFor } from '@shared/ai/models';
import {
  surfacePalette,
  SurfacePaletteFullError,
  type SurfaceLayer,
  type SurfaceLayerDraft,
} from '@/terrain/TerrainSurface';
import { buildTextureRecipePrompt } from './TexturePrompts';
import {
  fallbackRecipe,
  parseTextureRecipe,
  TextureRecipeError,
  type TextureRecipe,
} from './TextureRecipe';
import { renderSwatch } from './SwatchClient';
import {
  canDrawSwatches,
  normalizeSwatch,
  synthesizeSwatch,
  SwatchImageError,
  type NormalizedSwatch,
} from './SwatchImage';

/**
 * Making a ground material out of a sentence.
 *
 * The forge is the one place that knows the whole sequence — research, render, normalise, register —
 * so the three callers that want a new texture (the ground panel's "describe a material" field, the
 * swatch upload, and the reconstruction pipeline's surfaces stage) each hand it a phrase and get
 * back a {@link SurfaceLayer} that is already in the palette and already on the ground.
 *
 * ## Degrading rather than failing
 *
 * Every step after the first has a fallback, and that is deliberate: the useful unit of work here is
 * "a material named what I asked for, in about the right colour, that I can paint with". Losing the
 * photographic swatch is a real loss but a small one next to losing the material.
 *
 * | Step | On failure |
 * | ---- | ---------- |
 * | Grounded research | {@link fallbackRecipe} — colour read out of the words |
 * | Swatch render | {@link synthesizeSwatch} — drawn from the recipe's own numbers |
 * | Swatch decode | no swatch; the layer renders as its flat average colour |
 * | Palette full | throws — this one the user has to resolve, and the message says how |
 *
 * What each attempt actually did is reported on the result rather than swallowed, so the panel can
 * say "the search failed, so this colour is a guess" instead of quietly presenting an invention as
 * research.
 */

/** The model that does the grounded research. Balanced tier: this is a per-material call. */
export const DEFAULT_FORGE_MODEL = defaultModelFor('forge');

/** Ceiling on the recipe reply. A recipe is twenty short fields; this is generous for the search. */
const MAX_RECIPE_TOKENS = 3000;

export interface ForgeTextureRequest {
  /** What was asked for, in words: "red gravel", "weathered bluestone pavers". */
  request: string;
  /** Null runs the whole thing offline — a named material in a guessed colour, drawn procedurally. */
  apiKey: string | null;
  /** Where the material is going, when the caller knows. Sharpens the research. */
  context?: string;
  model?: string;
  /** Skip the paid image render and draw the swatch from the recipe instead. */
  proceduralOnly?: boolean;
  signal?: AbortSignal;
  /** Injected for tests. */
  callImpl?: typeof callVisionModel;
  renderImpl?: typeof renderSwatch;
}

/** What the forge produced, and how much of it was actually grounded. */
export interface ForgedTexture {
  layer: SurfaceLayer;
  recipe: TextureRecipe;
  /** `researched` searched the web; `guessed` read the colour out of the phrase. */
  recipeSource: 'researched' | 'guessed';
  /** `rendered` came from the image model; `synthesized` was drawn; `none` means flat colour only. */
  swatchSource: 'rendered' | 'synthesized' | 'none';
  /** Anything that degraded, in the order it happened. Shown in the panel; never thrown away. */
  notes: string[];
}

/**
 * Research a material, render its swatch, and put it in the palette.
 *
 * Throws only for {@link SurfacePaletteFullError} and for an aborted call. Everything else lands in
 * `notes` and the material is still made.
 */
export async function forgeTexture(request: ForgeTextureRequest): Promise<ForgedTexture> {
  const phrase = request.request.trim();
  if (phrase.length === 0) {
    throw new TextureRecipeError('Describe the material you want — "red gravel", say.');
  }
  if (surfacePalette.freeSlots <= 0) {
    throw new SurfacePaletteFullError(
      'This site has no room for another painted material. Remove one from the ground panel first.',
    );
  }

  const notes: string[] = [];
  const researched = await researchRecipe(phrase, request, notes);
  const swatch = await makeSwatch(researched.recipe, request, notes);

  const draft: SurfaceLayerDraft = {
    name: researched.recipe.name,
    // The measured average of the swatch beats the model's stated colour whenever there is a swatch:
    // the chip, the brush ring and the plan overlay then agree with what the ground actually shows.
    color: swatch.result?.averageColor ?? researched.recipe.color,
    roughness: researched.recipe.roughness,
    plantable: researched.recipe.plantable,
    hint: researched.recipe.hint,
    character: researched.recipe.character,
    ...(swatch.result ? { swatch: swatch.result.swatch } : {}),
    origin: {
      kind: 'forged',
      request: phrase,
      ...(researched.recipe.grounding ? { grounding: researched.recipe.grounding } : {}),
      createdAt: new Date().toISOString(),
    },
  };

  return {
    layer: surfacePalette.add(draft),
    recipe: researched.recipe,
    recipeSource: researched.source,
    swatchSource: swatch.source,
    notes,
  };
}

/**
 * Register a swatch the user supplied, with no model involved.
 *
 * The escape hatch the forge needs to be honest about its own limits: someone who has a photograph
 * of the exact gravel on the exact job does not want a model's impression of it. The name and the
 * physical properties still have to come from somewhere, and here they come from the caller — the
 * ground panel asks for them next to the file picker.
 */
export async function importSwatch(input: {
  file: Blob;
  name: string;
  /** File name or similar, recorded as the material's origin. */
  sourceLabel: string;
  tileMeters: number;
  roughness: number;
  plantable: boolean;
  /** Set when the file is already known to tile, which skips the seam blend. */
  alreadyTileable?: boolean;
}): Promise<SurfaceLayer> {
  if (surfacePalette.freeSlots <= 0) {
    throw new SurfacePaletteFullError(
      'This site has no room for another painted material. Remove one from the ground panel first.',
    );
  }
  const normalized = await normalizeSwatch(input.file, input.tileMeters, {
    ...(input.alreadyTileable ? { seamBand: 0 } : {}),
  });
  const name = input.name.trim() || 'Uploaded material';

  return surfacePalette.add({
    name,
    color: normalized.averageColor,
    roughness: input.roughness,
    plantable: input.plantable,
    hint: `Uploaded swatch, tiled every ${input.tileMeters.toFixed(2)} m.`,
    character: `${name.toLowerCase()} ground surface`,
    swatch: normalized.swatch,
    origin: {
      kind: 'uploaded',
      request: input.sourceLabel,
      createdAt: new Date().toISOString(),
    },
  });
}

/* --------------------------------- The steps --------------------------------- */

async function researchRecipe(
  phrase: string,
  request: ForgeTextureRequest,
  notes: string[],
): Promise<{ recipe: TextureRecipe; source: 'researched' | 'guessed' }> {
  if (!request.apiKey) {
    notes.push('No API key, so the colour was read out of the words rather than researched.');
    return { recipe: fallbackRecipe(phrase), source: 'guessed' };
  }

  const context = request.context?.trim();
  const prompt = buildTextureRecipePrompt({
    request: phrase,
    ...(context ? { context } : {}),
  });

  try {
    const call = await (request.callImpl ?? callVisionModel)({
      apiKey: request.apiKey,
      operation: 'forge',
      prompt,
      images: [],
      allowTextOnly: true,
      webSearch: true,
      maxOutputTokens: MAX_RECIPE_TOKENS,
      model: request.model ?? DEFAULT_FORGE_MODEL,
      ...(request.signal ? { signal: request.signal } : {}),
    });
    return { recipe: parseTextureRecipe(JSON.parse(extractJson(call.raw))), source: 'researched' };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    notes.push(`The material search failed (${describe(error)}), so the colour is a guess.`);
    return { recipe: fallbackRecipe(phrase), source: 'guessed' };
  }
}

async function makeSwatch(
  recipe: TextureRecipe,
  request: ForgeTextureRequest,
  notes: string[],
): Promise<{ result: NormalizedSwatch | null; source: ForgedTexture['swatchSource'] }> {
  if (!canDrawSwatches()) {
    // Headless: the layer is still perfectly usable, it simply renders as its average colour.
    return { result: null, source: 'none' };
  }

  const synthesize = (): { result: NormalizedSwatch | null; source: ForgedTexture['swatchSource'] } => {
    try {
      return { result: synthesizeSwatch(recipe), source: 'synthesized' };
    } catch (error) {
      notes.push(`The swatch could not be drawn (${describe(error)}); the material is a flat colour.`);
      return { result: null, source: 'none' };
    }
  };

  if (!request.apiKey || request.proceduralOnly === true) return synthesize();

  try {
    const png = await (request.renderImpl ?? renderSwatch)({
      apiKey: request.apiKey,
      prompt: recipe.imagePrompt,
      ...(request.signal ? { signal: request.signal } : {}),
    });
    return { result: await normalizeSwatch(png, recipe.tileMeters), source: 'rendered' };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    notes.push(`The swatch render failed (${describe(error)}), so it was drawn from the description.`);
    return synthesize();
  }
}

function describe(error: unknown): string {
  if (error instanceof SitePlanError) return error.message;
  if (error instanceof TextureRecipeError) return error.message;
  if (error instanceof SwatchImageError) return error.message;
  if (error instanceof Error) return error.message;
  return 'unknown error';
}
